import apiService from "./api";
import ritualService, { type RitualStats } from "./ritualService";
import { getDashboardSummary } from "./ticketService";
import orixaService, { type OrixaDistribution } from "./orixaService";

export interface PersonStats {
	totalPersons: number;
	totalConsultants: number;
	totalMediums: number;
}

export interface MonthlyStat {
	month: string;
	label: string;
	total: number;
}

export interface WeeklyStat {
	week: string;
	label: string;
	total: number;
}

export interface TopConsultant {
	id: string;
	firstName: string;
	lastName: string;
	nickname?: string;
	photoUrl?: string;
	ticketCount: number;
}

export interface DashboardData {
	personStats: PersonStats;
	ritualStats: RitualStats;
	ticketSummary: { totalTickets: number; pendingTickets: number; completedTickets: number; cancelledTickets: number };
	monthlyPersons: MonthlyStat[];
	monthlyTickets: MonthlyStat[];
	weeklyConsultants: WeeklyStat[];
	topConsultants: TopConsultant[];
	orixaDistribution: OrixaDistribution[];
}

export const getPersonStats = async (): Promise<PersonStats> => {
	return apiService.get("/persons/stats");
};

export const getMonthlyPersons = async (months = 6): Promise<MonthlyStat[]> => {
	return apiService.get("/persons/monthly", { params: { months } });
};

export const getMonthlyTickets = async (months = 6): Promise<MonthlyStat[]> => {
	return apiService.get("/tickets/monthly", { params: { months } });
};

export const getWeeklyConsultants = async (weeks = 8): Promise<WeeklyStat[]> => {
	return apiService.get("/persons/weekly-consultants", { params: { weeks } });
};

export const getTopConsultants = async (limit = 5): Promise<TopConsultant[]> => {
	return apiService.get("/persons/top-consultants", { params: { limit } });
};

export const getDashboardData = async (): Promise<DashboardData> => {
	const [personStats, ritualStats, ticketSummary, monthlyPersons, monthlyTickets, weeklyConsultants, topConsultants, orixaDistribution] =
		await Promise.all([
			getPersonStats(),
			ritualService.getRitualStats(),
			getDashboardSummary(),
			getMonthlyPersons(),
			getMonthlyTickets(),
			getWeeklyConsultants(),
			getTopConsultants(),
			orixaService.getDistribution(),
		]);

	return {
		personStats,
		ritualStats,
		ticketSummary,
		monthlyPersons,
		monthlyTickets,
		weeklyConsultants,
		topConsultants,
		orixaDistribution,
	};
};
